// @pages/workspace/AccountManagement.page.tsx

import React, { useCallback, useMemo } from "react";
import { useAccountClient } from "@contexts/AccountClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useIoState } from "@utils/useIoState";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useAdminUserClient } from "@contexts/AdminUserClient.context";
import AccountManagementComponent from "@view/workspace/AccountManagement";
import type {
    PasswordResetCompleteRequest,
    PasswordResetError,
    PasswordResetStartRequest,
    AccountResponse,
} from "@ps-bridge/models/core/dataContracts";
import type { OptionTs, TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import { ok, type Ok } from "@ps-bridge/ok";
import { mkTaskRunner } from "@ps-bridge/capabilities";
import {
    type PasswordResetCompleteFetch,
    type PasswordResetRequestFetch,
} from "@ps-bridge/contracts/accountManagement";
import { ioEffect, taskEitherFromEffect } from "@utils/effects/branded";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import * as PSOption from "@utils/Option";
import type { AccountManagementKernel, AccountMembership } from "@ps-bridge/contracts/accountManagement";
import { buildAccountManagementViewProps } from "@ps-bridge/accountManagement.program";
import { optionToPsMaybe } from "@src/purescript/interop";

const toPsMaybeStatusCode = <T,>(value: OptionTs<T>): OptionTs<T> =>
    optionToPsMaybe(value);

const normalizePasswordResetError = (error: PasswordResetError): PasswordResetError => ({
    ...error,
    statusCode: toPsMaybeStatusCode(error.statusCode),
});

const AccountManagementPage: React.FC = () => {
    const accountClient = useAccountClient();
    const authenticationClient = useAuthenticationClient();
    const adminUserClient = useAdminUserClient();
    const loading = useLoadingController();

    const accountState = useIoOptionState<AccountResponse>(PSOption.none());
    const membershipState = useIoOptionState<AccountMembership>(PSOption.none());
    const passwordStepState = useIoState<string>("request");
    const passwordResetCompletedState = useIoState<boolean>(false);
    const passwordSubmittingState = useIoState<boolean>(false);

    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);

    const requestPasswordReset = useCallback(
        (request: PasswordResetStartRequest): TaskEitherTs<PasswordResetError, Ok> =>
            taskEitherFromEffect(
                authenticationClient.requestPasswordReset(request),
                (error) => normalizePasswordResetError(error as PasswordResetError),
            ),
        [authenticationClient],
    );

    const completePasswordReset = useCallback(
        (request: PasswordResetCompleteRequest): TaskEitherTs<PasswordResetError, Ok> =>
            taskEitherFromEffect(
                authenticationClient.completePasswordReset(request),
                (error) => normalizePasswordResetError(error as PasswordResetError),
            ),
        [authenticationClient],
    );

    const kernel: AccountManagementKernel = useMemo(
        () => ({
            loading,
            accountClient,
            adminUserClient,
            requestPasswordReset: requestPasswordReset as PasswordResetRequestFetch,
            completePasswordReset: completePasswordReset as PasswordResetCompleteFetch,
            accountDetails: {
                read: accountState.read,
                setSome: accountState.setSome,
                setMaybe: accountState.setMaybe,
            },
            membership: {
                read: membershipState.read,
                setSome: membershipState.setSome,
                setMaybe: membershipState.setMaybe,
            },
            passwordStep: {
                read: passwordStepState.read,
                set: passwordStepState.set,
            },
            passwordResetCompleted: {
                read: passwordResetCompletedState.read,
                set: passwordResetCompletedState.set,
            },
            passwordSubmitting: {
                read: passwordSubmittingState.read,
                set: passwordSubmittingState.set,
            },
            taskRunner,
        }),
        [
            loading,
            accountClient,
            adminUserClient,
            requestPasswordReset,
            completePasswordReset,
            accountState.read,
            accountState.setMaybe,
            accountState.setSome,
            membershipState.read,
            membershipState.setMaybe,
            membershipState.setSome,
            passwordStepState.read,
            passwordStepState.set,
            passwordResetCompletedState.read,
            passwordResetCompletedState.set,
            passwordSubmittingState.read,
            passwordSubmittingState.set,
            taskRunner,
        ],
    );

    const viewProps = useMemo(() => buildAccountManagementViewProps({ kernel }), [kernel]);

    return <AccountManagementComponent {...viewProps} />;
};

export default AccountManagementPage;
